import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';
import {
  automationsFromLegacyFields,
  legacyFieldsFromAutomations,
  type QuestionnaireAutomationDraft,
} from '@/lib/preVisitQuestionnaireTiming';
import { assertServiceDurationMinutes } from '@/lib/serviceDuration';

export type ServiceRow = Database['public']['Tables']['services']['Row'] & {
  practitioner_eligibility?: 'all' | 'selected';
};

export interface ServiceInput {
  name: string;
  description?: string | null;
  duration_minutes: number;
  price_cents: number;
  currency?: string;
  vat_rate_percent?: number;
  color?: string;
  is_active?: boolean;
  /** If true, this is a calendar blocker (lunch, meeting, etc.) that doesn't require a patient. CLISUP-38. */
  is_blocker?: boolean;
  /** Forms template IDs to auto-send when an appointment of this type is completed. FORMS-8. */
  auto_send_questionnaires?: string[];
  /** Single Forms template ID to send when the patient books online. FORMS-10. */
  pre_visit_questionnaire_template_id?: string | null;
  /** Hours before the appointment to send the pre-visit email. 0 = send on booking. Empty while typing. */
  pre_visit_hours_before?: number | '';
  /** Hours after booking before the pre-visit questionnaire is sent. Empty while typing. */
  pre_visit_delay_after_booking_hours?: number | '';
  /** Per-questionnaire automations. Timing belongs to each questionnaire. */
  questionnaire_automations?: QuestionnaireAutomationDraft[];
  /** Optional override of clinic payment timing. null = inherit clinic default. */
  payment_timing?: 'upon_booking' | 'at_appointment' | null;
  deposit_amount_cents?: number;
}

function hoursOrZero(value: number | '' | undefined): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.min(8760, value))
    : 0;
}

function payloadFromInput(input: ServiceInput, clinicId: string) {
  const automations =
    input.questionnaire_automations ??
    automationsFromLegacyFields({
      pre_visit_questionnaire_template_id: input.pre_visit_questionnaire_template_id,
      pre_visit_hours_before: input.pre_visit_hours_before,
      pre_visit_delay_after_booking_hours: input.pre_visit_delay_after_booking_hours,
      auto_send_questionnaires: input.auto_send_questionnaires,
    });
  const legacy = legacyFieldsFromAutomations(automations);
  return {
    clinic_id: clinicId,
    name: input.name,
    description: input.description ?? null,
    duration_minutes: assertServiceDurationMinutes(input.duration_minutes),
    price_cents: input.price_cents,
    currency: input.currency ?? 'DKK',
    vat_rate_percent: input.vat_rate_percent ?? 25,
    color: input.color ?? '#6366F1',
    is_active: input.is_active ?? true,
    is_blocker: input.is_blocker ?? false,
    auto_send_questionnaires: legacy.auto_send_questionnaires,
    pre_visit_questionnaire_template_id: legacy.pre_visit_questionnaire_template_id,
    pre_visit_hours_before: hoursOrZero(legacy.pre_visit_hours_before),
    pre_visit_delay_after_booking_hours: hoursOrZero(legacy.pre_visit_delay_after_booking_hours),
    payment_timing: input.payment_timing ?? null,
    deposit_amount_cents: input.deposit_amount_cents ?? 0,
    automations,
  };
}

async function replaceQuestionnaireAutomations(
  clinicId: string,
  serviceId: string,
  automations: QuestionnaireAutomationDraft[],
) {
  const { error } = await supabase.rpc('replace_service_questionnaire_automations', {
    p_clinic_id: clinicId,
    p_service_id: serviceId,
    p_automations: automations.map((row, index) => ({
      forms_template_id: row.forms_template_id,
      phase: row.phase,
      offset_minutes: row.offset_minutes,
      delay_after_booking_minutes: 0,
      is_active: row.is_active,
      sort_order: row.sort_order || index,
    })),
  });
  if (error) throw error;
}

export const servicesListQueryKey = (clinicId: string | null, includeInactive: boolean) =>
  ['services', 'list', clinicId, { includeInactive }] as const;

export const serviceQuestionnaireAutomationsQueryKey = (clinicId: string | null) =>
  ['service-questionnaire-automations', clinicId] as const;

export type ServiceQuestionnaireAutomation = QuestionnaireAutomationDraft & {
  service_id: string;
};

export function useServiceQuestionnaireAutomations(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: serviceQuestionnaireAutomationsQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ServiceQuestionnaireAutomation[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('service_questionnaire_automations')
        .select(
          'id, service_id, forms_template_id, phase, offset_minutes, delay_after_booking_minutes, is_active, sort_order',
        )
        .eq('clinic_id', clinicId)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as ServiceQuestionnaireAutomation[];
    },
    staleTime: 60_000,
  });
}

export function useServicesList(
  clinicId: string | null | undefined,
  options: { includeInactive?: boolean } = {},
) {
  const includeInactive = Boolean(options.includeInactive);
  return useQuery({
    queryKey: servicesListQueryKey(clinicId ?? null, includeInactive),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ServiceRow[]> => {
      if (!clinicId) return [];
      let query = supabase
        .from('services')
        .select('*')
        .eq('clinic_id', clinicId)
        .order('name', { ascending: true });

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });
}

export function useCreateService(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ServiceInput): Promise<ServiceRow> => {
      if (!clinicId) throw new Error('No active clinic');
      const payload = payloadFromInput(input, clinicId);
      const { automations, ...insert } = payload;
      const { data, error } = await supabase.from('services').insert(insert).select('*').single();
      if (error) throw error;
      await replaceQuestionnaireAutomations(clinicId, data.id, automations);
      return data as ServiceRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['services', 'list', clinicId ?? null] });
      qc.invalidateQueries({ queryKey: ['public-booking'] });
      qc.invalidateQueries({ queryKey: serviceQuestionnaireAutomationsQueryKey(clinicId ?? null) });
    },
  });
}

export function useUpdateService(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<ServiceInput>): Promise<ServiceRow> => {
      if (!clinicId) throw new Error('No active clinic');
      const {
        id,
        auto_send_questionnaires,
        pre_visit_questionnaire_template_id,
        pre_visit_hours_before,
        pre_visit_delay_after_booking_hours,
        questionnaire_automations,
        ...rest
      } = input;

      const automations =
        questionnaire_automations ??
        automationsFromLegacyFields({
          pre_visit_questionnaire_template_id,
          pre_visit_hours_before,
          pre_visit_delay_after_booking_hours,
          auto_send_questionnaires,
        });
      const legacy = legacyFieldsFromAutomations(automations);

      if (rest.duration_minutes != null) {
        assertServiceDurationMinutes(rest.duration_minutes);
      }

      type ServiceUpdate = Database['public']['Tables']['services']['Update'];
      const patch: Partial<ServiceUpdate> = { ...rest };
      patch.auto_send_questionnaires = legacy.auto_send_questionnaires;
      patch.pre_visit_questionnaire_template_id = legacy.pre_visit_questionnaire_template_id;
      patch.pre_visit_hours_before = hoursOrZero(legacy.pre_visit_hours_before);
      patch.pre_visit_delay_after_booking_hours = hoursOrZero(
        legacy.pre_visit_delay_after_booking_hours,
      );

      const { data, error } = await supabase
        .from('services')
        .update(patch)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      await replaceQuestionnaireAutomations(clinicId, id, automations);
      return data as ServiceRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['services', 'list', clinicId ?? null] });
      qc.invalidateQueries({ queryKey: ['public-booking'] });
      qc.invalidateQueries({ queryKey: serviceQuestionnaireAutomationsQueryKey(clinicId ?? null) });
    },
  });
}

export function useDeleteService(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('services').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['services', 'list', clinicId ?? null] });
      qc.invalidateQueries({ queryKey: ['public-booking'] });
    },
  });
}
